import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild, inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject, fromEvent, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { ChatFriend, ChatMember, ChatMessage, ChatRelations, ChatSearchResult, memberName } from 'src/app/models/chat';
import { AuthService } from 'src/app/services/auth.service';
import { CallService } from 'src/app/services/call.service';
import { ChatPushService } from 'src/app/services/chat-push.service';
import { ChatService } from 'src/app/services/chat.service';
import { I18nService } from 'src/app/services/i18n.service';
import { DialogContentLogin } from '../header-sticky/login-dialog/dialog-content-login';

type MessagesTab = 'chats' | 'requests' | 'search';

const DEFAULT_AVATAR = 'assets/userAvatar.svg';
const TYPING_IDLE_MS = 3000;
const TYPING_DISPLAY_MS = 5000;

// Messagerie entre amis : liste des discussions, demandes d'ami, recherche de membres et conversation
// en temps réel. Sur mobile, une seule colonne à la fois (liste, ou conversation via /messages/:userId).
@Component({
  selector: 'app-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MessagesComponent implements OnInit, OnDestroy {

  @ViewChild('messageList') messageList?: ElementRef<HTMLElement>;

  isLoggedIn = false;
  myId: string | null = null;
  tab: MessagesTab = 'chats';
  relations: ChatRelations | null = null;

  searchText = '';
  searchResults: ChatSearchResult[] = [];
  searching = false;
  private search$ = new Subject<string>();

  friendId: string | null = null;
  messages: ChatMessage[] = [];
  hasMore = false;
  loadingMessages = false;
  loadingOlder = false;
  newMessage = '';
  sending = false;
  friendTyping = false;
  showPushBanner = false;
  activatingPush = false;

  private busyIds = new Set<string>();
  private typingSent = false;
  private typingIdleTimer: ReturnType<typeof setTimeout> | null = null;
  private typingDisplayTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyRef = inject(DestroyRef);

  constructor(
    private chatService: ChatService,
    private callService: CallService,
    private chatPush: ChatPushService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private i18n: I18nService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.authService.currentUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.isLoggedIn = !!user;
      this.myId = user?._id ?? null;
      if (!user) {
        this.resetConversation();
      }
    });

    this.chatPush.canActivate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(canActivate => this.showPushBanner = canActivate);

    this.chatService.relations$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(relations => {
      this.relations = relations;
      // Ami retiré pendant que la conversation est ouverte
      if (relations && this.friendId && !this.friend) {
        this.router.navigate(['/messages']);
      }
    });

    // Sans ?tab= (lien « Discussions », /messages) on revient sur la liste des discussions
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const tab = params.get('tab');
      this.tab = tab === 'requests' || tab === 'search' ? tab : 'chats';
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => this.openConversation(params.get('userId')));

    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(text => {
        if (text.trim().length < 2) {
          this.searching = false;
          return of([] as ChatSearchResult[]);
        }
        this.searching = true;
        return this.chatService.searchUsers(text.trim()).pipe(catchError(() => of([] as ChatSearchResult[])));
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(results => {
      this.searchResults = results;
      this.searching = false;
    });

    this.chatService.messages$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(message => this.onMessage(message));

    this.chatService.read$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (!this.friendId || event.conversationKey !== this.chatService.conversationKey(this.friendId)) {
        return;
      }
      // Mes messages lus par l'ami : affiche « Vu »
      this.messages = this.messages.map(message =>
        String(message.to) === String(event.reader) && !message.readAt ? { ...message, readAt: event.readAt } : message);
    });

    this.chatService.typing$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (String(event.from) !== this.friendId) {
        return;
      }
      this.friendTyping = event.typing;
      this.clearTimer('display');
      if (event.typing) {
        this.typingDisplayTimer = setTimeout(() => this.friendTyping = false, TYPING_DISPLAY_MS);
      }
    });

    // Retour sur l'onglet : marque comme lus les messages arrivés pendant l'absence
    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.markReadIfNeeded());
  }

  ngOnDestroy(): void {
    this.stopTyping();
    this.clearTimer('display');
    this.chatService.activeConversation = null;
  }

  // ----- Liste et onglets -----

  get friends(): ChatFriend[] {
    return this.relations?.friends ?? [];
  }

  get friend(): ChatFriend | undefined {
    return this.friends.find(friend => String(friend.user._id) === this.friendId);
  }

  get requestsCount(): number {
    return this.relations?.incoming.length ?? 0;
  }

  setTab(tab: MessagesTab): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab: tab === 'chats' ? null : tab }, queryParamsHandling: 'merge' });
  }

  onSearchInput(): void {
    this.search$.next(this.searchText);
  }

  login(): void {
    this.dialog.open(DialogContentLogin, { width: '550px', data: {} });
  }

  name(member: ChatMember | undefined | null): string {
    return memberName(member);
  }

  avatar(member: ChatMember | undefined | null): string {
    return member?.avatar || DEFAULT_AVATAR;
  }

  preview(friend: ChatFriend): string {
    if (!friend.lastMessage) {
      return this.i18n.t('chat.startConversation');
    }
    const mine = String(friend.lastMessage.from) === this.myId;
    return mine ? this.i18n.t('chat.you', { text: friend.lastMessage.text }) : friend.lastMessage.text;
  }

  isBusy(id: string): boolean {
    return this.busyIds.has(id);
  }

  // ----- Demandes d'ami -----

  sendRequest(result: ChatSearchResult): void {
    this.run(result.user._id, this.chatService.sendRequest(result.user._id), () => {
      result.relation = this.chatService.relationOf(result.user._id);
      result.requestId = this.findRequestId(result.user._id);
      this.toast(result.relation === 'friend'
        ? this.i18n.t('chat.nowFriends', { pseudo: this.name(result.user) })
        : this.i18n.t('chat.requestSentTo', { pseudo: this.name(result.user) }));
    });
  }

  acceptRequest(requestId: string, member: ChatMember, result?: ChatSearchResult): void {
    this.run(requestId, this.chatService.acceptRequest(requestId), () => {
      if (result) {
        result.relation = 'friend';
        result.requestId = null;
      }
      this.toast(this.i18n.t('chat.nowFriends', { pseudo: this.name(member) }));
    });
  }

  deleteRequest(requestId: string, result?: ChatSearchResult): void {
    this.run(requestId, this.chatService.deleteRequest(requestId), () => {
      if (result) {
        result.relation = 'none';
        result.requestId = null;
      }
    });
  }

  removeFriend(): void {
    const friend = this.friend;
    if (!friend || !confirm(this.i18n.t('chat.removeFriendConfirm', { pseudo: this.name(friend.user) }))) {
      return;
    }
    this.run(friend.user._id, this.chatService.removeFriend(friend.user._id), () => {
      this.toast(this.i18n.t('chat.friendRemoved', { pseudo: this.name(friend.user) }));
      this.router.navigate(['/messages']);
    });
  }

  // ----- Appels -----

  // Un ami hors ligne reste appelable : le serveur le prévient par notification, ou répond qu'il est injoignable
  get canCall(): boolean {
    return !!this.friend && !this.callService.current;
  }

  async activatePush(): Promise<void> {
    this.activatingPush = true;
    try {
      await this.chatPush.activate();
      this.snackBar.open(this.i18n.t('header.pushEnabled'), this.i18n.t('common.ok'), { duration: 6000 });
    } catch (err) {
      console.error('Could not activate notifications:', err);
      const key = this.chatPush.permission === 'denied' ? 'header.pushDenied' : 'header.pushError';
      this.snackBar.open(this.i18n.t(key), this.i18n.t('common.ok'), { duration: 6000 });
    }
    this.activatingPush = false;
  }

  dismissPushBanner(): void {
    this.chatPush.dismissBanner();
    this.showPushBanner = false;
  }

  startCall(video: boolean): void {
    const friend = this.friend;
    if (friend && this.canCall) {
      this.callService.start(friend.user, video);
    }
  }

  // ----- Conversation -----

  private openConversation(userId: string | null): void {
    this.stopTyping();
    this.resetConversation();
    this.friendId = userId;
    this.chatService.activeConversation = userId;
    if (!userId || !this.isLoggedIn) {
      return;
    }
    this.loadingMessages = true;
    this.chatService.getMessages(userId).subscribe({
      next: page => {
        if (this.friendId !== userId) {
          return;
        }
        this.messages = page.messages;
        this.hasMore = page.hasMore;
        this.loadingMessages = false;
        this.scrollToBottom();
        this.markReadIfNeeded();
      },
      error: err => {
        this.loadingMessages = false;
        this.toast(err || this.i18n.t('common.operationUnavailable'));
        this.router.navigate(['/messages']);
      },
    });
  }

  loadOlder(): void {
    if (!this.friendId || this.loadingOlder || !this.messages.length) {
      return;
    }
    const friendId = this.friendId;
    const list = this.messageList?.nativeElement;
    const previousHeight = list ? list.scrollHeight : 0;
    this.loadingOlder = true;
    this.chatService.getMessages(friendId, this.messages[0].createdAt).subscribe({
      next: page => {
        this.loadingOlder = false;
        if (this.friendId !== friendId) {
          return;
        }
        this.messages = [...page.messages, ...this.messages];
        this.hasMore = page.hasMore;
        // Garde la position de lecture une fois les anciens messages insérés au-dessus
        setTimeout(() => {
          if (list) {
            list.scrollTop = list.scrollHeight - previousHeight;
          }
        });
      },
      error: () => this.loadingOlder = false,
    });
  }

  onComposerKeydown(event: KeyboardEvent): void {
    // Entrée envoie, Maj+Entrée va à la ligne
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.send();
    }
  }

  onComposerInput(): void {
    if (!this.friendId) {
      return;
    }
    if (!this.typingSent && this.newMessage.trim()) {
      this.typingSent = true;
      this.chatService.sendTyping(this.friendId, true);
    }
    this.clearTimer('idle');
    this.typingIdleTimer = setTimeout(() => this.stopTyping(), TYPING_IDLE_MS);
  }

  send(): void {
    const text = this.newMessage.trim();
    const friendId = this.friendId;
    if (!text || !friendId || this.sending) {
      return;
    }
    this.sending = true;
    this.chatService.sendMessage(friendId, text).subscribe({
      next: message => {
        this.sending = false;
        this.newMessage = '';
        this.stopTyping();
        this.addMessage(message);
      },
      error: err => {
        this.sending = false;
        this.toast(err || this.i18n.t('chat.sendError'));
      },
    });
  }

  isMine(message: ChatMessage): boolean {
    return String(message.from) === this.myId;
  }

  // « Vu » seulement sous mon dernier message lu
  isLastReadMine(index: number): boolean {
    const message = this.messages[index];
    if (!this.isMine(message) || !message.readAt) {
      return false;
    }
    return !this.messages.slice(index + 1).some(next => this.isMine(next));
  }

  // Nom de l'auteur au-dessus du premier message de chaque suite de messages d'une même personne
  showSender(index: number): boolean {
    return this.showDaySeparator(index) || this.isMine(this.messages[index - 1]) !== this.isMine(this.messages[index]);
  }

  senderName(message: ChatMessage): string {
    return this.isMine(message) ? this.i18n.t('chat.me') : this.name(this.friend?.user);
  }

  showDaySeparator(index: number): boolean {
    if (index === 0) {
      return true;
    }
    return new Date(this.messages[index - 1].createdAt).toDateString() !== new Date(this.messages[index].createdAt).toDateString();
  }

  dayLabel(date: string): string {
    const day = new Date(date);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (day.toDateString() === today.toDateString()) {
      return this.i18n.t('chat.today');
    }
    if (day.toDateString() === yesterday.toDateString()) {
      return this.i18n.t('chat.yesterday');
    }
    return day.toLocaleDateString(this.i18n.current, { day: 'numeric', month: 'long', year: 'numeric' });
  }

  // Heure si aujourd'hui, sinon date courte (liste des discussions)
  shortTime(date: string): string {
    const day = new Date(date);
    if (day.toDateString() === new Date().toDateString()) {
      return day.toLocaleTimeString(this.i18n.current, { hour: '2-digit', minute: '2-digit' });
    }
    return day.toLocaleDateString(this.i18n.current, { day: '2-digit', month: '2-digit' });
  }

  messageTime(date: string): string {
    return new Date(date).toLocaleTimeString(this.i18n.current, { hour: '2-digit', minute: '2-digit' });
  }

  private onMessage(message: ChatMessage): void {
    if (!this.friendId) {
      return;
    }
    const inConversation = String(message.from) === this.friendId || String(message.to) === this.friendId;
    if (!inConversation) {
      return;
    }
    if (String(message.from) === this.friendId) {
      this.friendTyping = false;
    }
    this.addMessage(message);
    this.markReadIfNeeded();
  }

  // Le message envoyé revient aussi par le socket : on évite le doublon
  private addMessage(message: ChatMessage): void {
    if (this.messages.some(existing => existing._id === message._id)) {
      return;
    }
    const list = this.messageList?.nativeElement;
    const nearBottom = !list || list.scrollHeight - list.scrollTop - list.clientHeight < 120;
    this.messages = [...this.messages, message];
    if (nearBottom || this.isMine(message)) {
      this.scrollToBottom();
    }
  }

  private markReadIfNeeded(): void {
    if (!this.friendId || document.visibilityState !== 'visible') {
      return;
    }
    const unread = this.messages.some(message => String(message.from) === this.friendId && !message.readAt);
    if (unread) {
      const now = new Date().toISOString();
      this.messages = this.messages.map(message =>
        String(message.from) === this.friendId && !message.readAt ? { ...message, readAt: now } : message);
      this.chatService.markRead(this.friendId);
    }
  }

  private stopTyping(): void {
    this.clearTimer('idle');
    if (this.typingSent && this.friendId) {
      this.chatService.sendTyping(this.friendId, false);
    }
    this.typingSent = false;
  }

  private resetConversation(): void {
    this.messages = [];
    this.hasMore = false;
    this.newMessage = '';
    this.friendTyping = false;
    this.loadingMessages = false;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const list = this.messageList?.nativeElement;
      if (list) {
        list.scrollTop = list.scrollHeight;
      }
    });
  }

  private clearTimer(which: 'idle' | 'display'): void {
    if (which === 'idle' && this.typingIdleTimer) {
      clearTimeout(this.typingIdleTimer);
      this.typingIdleTimer = null;
    }
    if (which === 'display' && this.typingDisplayTimer) {
      clearTimeout(this.typingDisplayTimer);
      this.typingDisplayTimer = null;
    }
  }

  private findRequestId(userId: string): string | null {
    const relations = this.chatService.relationsValue;
    const same = (member: ChatMember) => String(member._id) === String(userId);
    return relations?.outgoing.find(request => same(request.user))?._id
      ?? relations?.incoming.find(request => same(request.user))?._id
      ?? null;
  }

  private run<T>(id: string, request: Observable<T>, done: () => void): void {
    if (this.busyIds.has(id)) {
      return;
    }
    this.busyIds.add(id);
    request.subscribe({
      next: () => {
        this.busyIds.delete(id);
        done();
      },
      error: err => {
        this.busyIds.delete(id);
        this.toast(err || this.i18n.t('common.operationUnavailable'));
      },
    });
  }

  private toast(message: string): void {
    this.snackBar.open(message, this.i18n.t('common.close'), { duration: 4000 });
  }
}
